/**
 * Build Your Box - box size selection, product picking, live price preview,
 * and real Recharge bundle submission.
 *
 * Capacity and discount % per box size come from Recharge itself
 * (syncFromRecharge(), fetched from Recharge's public bundle-settings CDN
 * endpoint) rather than being duplicated in the theme editor - Recharge is
 * the single source of truth, so the two can't drift out of sync. The
 * theme's capacity/discount_percent block settings only serve as a
 * fallback shown until that fetch resolves (or if it fails).
 *
 * Pricing shown here is client-side math for preview purposes only (matches
 * Recharge's own documented approach: storefront pricing is an estimate,
 * the real discount is enforced server-side by Recharge's Shopify Function
 * at checkout).
 *
 * submitBox() below does NOT use @rechargeapps/storefront-client at
 * runtime. Inspecting the SDK live (see getDynamicBundleItems source)
 * showed it's a pure, synchronous, non-network function - it just formats
 * four "_rc_bundle*" cart line-item properties from data we already have.
 * Loading a whole third-party CDN bundle for that isn't worth it, so the
 * same logic is reimplemented directly below and posted straight to
 * Shopify's own /cart/add.js. Recharge's backend watches for these
 * properties on cart/order line items and reassembles them into a priced
 * bundle at checkout.
 *
 * Subscription/selling-plan support is intentionally out of scope for this
 * pass (explicit product decision) - submission is one-time purchase only.
 */

import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, CartGroupedSections } from '@theme/events';
import { formatCurrency } from '@theme/utilities';

// Amounts throughout this file are decimal (e.g. 29.95), not cents, so convert before handing
// off to formatCurrency. Uses the shop's actual active-market format/currency (window.FoxTheme.moneyFormat,
// set in snippets/scripts.liquid from `shop.money_format`) instead of a fixed currency, so this
// matches the per-item prices that Liquid already renders correctly for the current market.
function money(amount) {
  return formatCurrency(Math.round(amount * 100), window.FoxTheme?.moneyFormat);
}

const BUNDLE_GROUP_ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

// Groups this submission's cart line items together for Recharge (the
// "_rc_bundle" property). Only needs to be unique per submission, not
// cryptographically random - matches the shape Recharge's own
// getDynamicBundleItems generates (a short random string), just not their
// exact charset/algorithm, which doesn't matter here.
function generateBundleGroupId(length) {
  let id = '';
  for (let i = 0; i < length; i++) {
    id += BUNDLE_GROUP_ID_CHARS[Math.floor(Math.random() * BUNDLE_GROUP_ID_CHARS.length)];
  }
  return id;
}

class BuildYourBox extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.limitMessage = this.dataset.limitMessage || '';
    this.bundleHandle = this.dataset.bundleHandle || '';
    this.bundleProductId = this.dataset.bundleProductId || '';
    this.collectionId = this.dataset.collectionId || '';
    this.storeIdentifier = this.dataset.storeIdentifier || '';

    this.sizeCards = Array.from(this.querySelectorAll('[data-box-size-card]'));
    this.activeSize = this.sizeCards.find((card) => card.classList.contains('is-selected')) || this.sizeCards[0];

    this.selected = [];

    this.addEventListener('click', this.onClick.bind(this));

    this.render();

    // Theme settings above are only the fallback shown until this resolves -
    // Recharge is the source of truth for capacity/discount so the two can't
    // drift out of sync with what's actually configured there.
    this.syncFromRecharge();
  }

  /**
   * Called by the theme's morph system (@theme/morph) after the Theme
   * Editor re-renders this section in place, e.g. after changing a block
   * setting. That morph copies the freshly-rendered HTML (including the
   * theme's fallback capacity/discount) straight onto the live DOM, since
   * it has no idea syncFromRecharge() had already overridden it - this
   * connectedCallback-only custom element is never removed/reinserted, so
   * without this hook the real Recharge values would get silently
   * clobbered back to the fallback every time a setting changes. Only
   * refreshes DOM references and re-applies the sync; selection state is
   * left untouched since the morph doesn't touch it.
   */
  updatedCallback() {
    this.sizeCards = Array.from(this.querySelectorAll('[data-box-size-card]'));
    this.activeSize =
      this.sizeCards.find((card) => card.dataset.rechargeVariantId === this.activeSize?.dataset.rechargeVariantId) ||
      this.sizeCards.find((card) => card.classList.contains('is-selected')) ||
      this.sizeCards[0];

    this.syncFromRecharge();
  }

  /**
   * Overrides each box size card's capacity/discount with live values from
   * Recharge's public bundle-settings endpoint, matched by Recharge variant
   * ID. Best-effort: on any failure (network, unexpected shape, missing
   * variant/tier), silently keeps the theme-configured fallback values.
   * Caches the fetched bundle data on the instance so repeat calls (e.g.
   * from updatedCallback firing on every Theme Editor setting change)
   * don't re-hit the network.
   */
  async syncFromRecharge() {
    if (!this.bundleProductId || !this.storeIdentifier) return;

    let bundle = this.rechargeBundleData;

    if (!bundle) {
      try {
        const response = await fetch(
          `https://static.rechargecdn.com/store/${this.storeIdentifier}/product/2020-12/${this.bundleProductId}.json`
        );
        if (!response.ok) throw new Error(`Recharge bundle config request failed (${response.status})`);
        const data = await response.json();
        bundle = data.product && data.product.bundle_product;
        if (!bundle) throw new Error('Unexpected Recharge bundle config shape');
        this.rechargeBundleData = bundle;
      } catch (err) {
        console.warn('build-your-box: could not load live Recharge bundle config, using theme fallback values', err);
        return;
      }
    }

    const variants = bundle.variants || [];
    const tiers = (bundle.incentives?.tiered_discounts?.[0]?.tiers || []).map((t) => t.tier);

    this.sizeCards.forEach((card) => {
      const variant = variants.find((v) => String(v.external_variant_id) === String(card.dataset.rechargeVariantId));
      if (!variant) return;

      // ranges[0].quantity_min/max is the reliable source for the real box
      // quantity - items_count looks similar but tracks something else
      // (seems to reflect default-selection count, which is 0 on a bundle
      // with none set) and isn't trustworthy for this.
      const capacity = variant.ranges?.[0]?.quantity_min ?? variant.items_count;
      const tier = tiers.find((t) => t.condition_quantity_gte === capacity);
      if (!tier) return;

      const discountPercent = Math.round(Number(tier.discount_value));

      card.dataset.capacity = String(capacity);
      card.dataset.discountPercent = String(discountPercent);

      const icon = card.querySelector('[data-capacity-icon]');
      if (icon) icon.textContent = capacity;

      const badge = card.querySelector('[data-save-badge]');
      if (badge && card.dataset.saveBadgeTemplate) {
        badge.textContent = card.dataset.saveBadgeTemplate.replace('%%PERCENT%%', discountPercent);
      }

      const discountCopy = card.querySelector('[data-discount-copy]');
      if (discountCopy && card.dataset.discountCopyTemplate) {
        discountCopy.textContent = card.dataset.discountCopyTemplate.replace('%%PERCENT%%', discountPercent);
      }
    });

    this.render();
  }

  onClick(event) {
    const sizeCard = event.target.closest('[data-box-size-card]');
    if (sizeCard) {
      this.selectSize(sizeCard);
      return;
    }

    const addButton = event.target.closest('[data-add-to-box]');
    if (addButton && !addButton.disabled) {
      this.addProduct(addButton);
      return;
    }

    const removeButton = event.target.closest('[data-remove-item]');
    if (removeButton) {
      this.removeProduct(removeButton.dataset.variantId);
      return;
    }

    const cta = event.target.closest('[data-summary-cta]');
    if (cta) {
      if (!cta.disabled) this.submitBox();
      return;
    }

    // Tapping the collapsed mobile summary bar expands it; the drag handle
    // area is the only click target when collapsed, so this only fires
    // when there's something to expand.
    const summaryPanel = event.target.closest('[data-summary-panel]');
    if (summaryPanel && window.matchMedia('(max-width: 767px)').matches) {
      summaryPanel.classList.toggle('is-expanded');
    }
  }

  selectSize(card) {
    if (card === this.activeSize) return;

    this.sizeCards.forEach((c) => c.classList.remove('is-selected'));
    card.classList.add('is-selected');
    this.activeSize = card;

    // Switching size clears the current selection - the simplest
    // predictable behavior when the new capacity is smaller than what's
    // already chosen. Revisit if the client wants selections preserved
    // across a size change instead.
    this.selected = [];

    this.render();
  }

  totalQuantity() {
    return this.selected.reduce((sum, item) => sum + item.quantity, 0);
  }

  addProduct(button) {
    const capacity = Number(this.activeSize.dataset.capacity);

    if (this.totalQuantity() >= capacity) {
      if (this.limitMessage) alert(this.limitMessage);
      return;
    }

    const variantId = button.dataset.variantId;
    const existing = this.selected.find((item) => item.variantId === variantId);

    if (existing) {
      existing.quantity += 1;
    } else {
      this.selected.push({
        variantId,
        title: button.dataset.productTitle,
        price: Number(button.dataset.price),
        quantity: 1,
      });
    }

    this.render();
  }

  removeProduct(variantId) {
    this.selected = this.selected.filter((item) => item.variantId !== variantId);
    this.render();
  }

  render() {
    const capacity = Number(this.activeSize.dataset.capacity);
    const discountPercent = Number(this.activeSize.dataset.discountPercent);
    const heading = this.activeSize.dataset.heading;
    const count = this.totalQuantity();
    const percentFull = capacity > 0 ? Math.min(100, Math.round((count / capacity) * 100)) : 0;
    const subtotal = this.selected.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discount = subtotal * (discountPercent / 100);
    const total = subtotal - discount;
    const remaining = Math.max(0, capacity - count);
    const isFull = count > 0 && count >= capacity;

    this.updateStepTracker(count, isFull);
    this.updateProductsHeading(capacity, heading, count, percentFull, isFull);
    this.updateProductCards(isFull);
    this.updateSummaryPanel({ heading, discountPercent, count, discount, total, remaining, isFull });
  }

  updateProductCards(isFull) {
    const selectedByVariant = new Map(this.selected.map((item) => [item.variantId, item]));

    this.querySelectorAll('[data-product-card]').forEach((card) => {
      const button = card.querySelector('[data-add-to-box]');
      const quantityBadge = card.querySelector('[data-quantity-badge]');
      if (!button) return;

      const item = selectedByVariant.get(card.dataset.variantId);
      card.classList.toggle('is-selected', Boolean(item));
      if (quantityBadge) quantityBadge.textContent = item ? item.quantity : '';

      // Once the box is full, every "Add to Box" button is disabled -
      // selected or not - since clicking it again is how quantity goes up.
      // Sold-out buttons stay as-is either way.
      button.disabled = isFull && !button.dataset.soldOut;
    });
  }

  updateStepTracker(count, isFull) {
    this.querySelectorAll('[data-step]').forEach((step) => {
      const stepNum = Number(step.dataset.step);
      const isActive = stepNum === 1 || (stepNum === 2 && count > 0) || (stepNum === 3 && isFull);
      step.classList.toggle('is-active', isActive);
    });
  }

  updateProductsHeading(capacity, heading, count, percentFull, isFull) {
    const subheading = this.querySelector('[data-products-subheading]');
    if (subheading) {
      subheading.textContent = isFull
        ? `Your ${heading} is full — remove an item to swap it, or add your box below.`
        : `Select ${capacity} products to fill your ${heading}.`;
    }
    this.classList.toggle('is-full', isFull);

    const progressCount = this.querySelector('[data-progress-count]');
    if (progressCount) progressCount.textContent = `${count} of ${capacity} selected`;

    const progressPercent = this.querySelector('[data-progress-percent]');
    if (progressPercent) progressPercent.textContent = `${percentFull}%`;

    const progressFill = this.querySelector('[data-progress-fill]');
    if (progressFill) progressFill.style.width = `${percentFull}%`;
  }

  updateSummaryPanel({ heading, discountPercent, count, discount, total, remaining, isFull }) {
    const sizeName = this.querySelector('[data-summary-size-name]');
    if (sizeName) sizeName.textContent = heading;

    const sizeBadge = this.querySelector('[data-summary-size-badge]');
    if (sizeBadge) sizeBadge.textContent = `SAVE ${discountPercent}%`;

    const itemsList = this.querySelector('[data-summary-items]');
    if (itemsList) {
      itemsList.innerHTML = '';
      this.selected.forEach((item) => {
        const li = document.createElement('li');
        const nameSpan = document.createElement('span');
        nameSpan.className = 'build-your-box__summary-item-name';
        nameSpan.textContent = `${item.title} × ${item.quantity}`;
        const priceSpan = document.createElement('span');
        priceSpan.className = 'build-your-box__summary-item-price';
        priceSpan.textContent = money(item.price * item.quantity);
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'build-your-box__summary-remove';
        removeBtn.dataset.removeItem = '';
        removeBtn.dataset.variantId = item.variantId;
        removeBtn.setAttribute('aria-label', `Remove ${item.title}`);
        removeBtn.textContent = '×';
        li.appendChild(nameSpan);
        li.appendChild(priceSpan);
        li.appendChild(removeBtn);
        itemsList.appendChild(li);
      });
    }

    const discountEl = this.querySelector('[data-summary-discount]');
    if (discountEl) discountEl.textContent = `-${money(discount)}`;

    const totalEl = this.querySelector('[data-summary-total]');
    if (totalEl) totalEl.textContent = money(total);

    const cta = this.querySelector('[data-summary-cta]');
    if (cta) {
      cta.disabled = !isFull;
      cta.classList.toggle('is-ready', isFull);
      cta.textContent = isFull ? 'Add Box to Cart' : `Select ${remaining} more item${remaining === 1 ? '' : 's'}`;
    }
  }

  submitBox() {
    if (!this.selected.length) return;

    if (!this.bundleHandle || !this.bundleProductId) {
      console.error(
        'build-your-box: missing Recharge bundle handle/product ID - set them on the section in Theme Editor.'
      );
      return;
    }

    const boxVariantId = this.activeSize.dataset.rechargeVariantId;
    if (!boxVariantId) {
      console.error(
        'build-your-box: missing Recharge variant ID for the selected box size - set it on that box-size-card block in Theme Editor.'
      );
      return;
    }

    // Matches the "_rc_bundle*" cart line-item property format confirmed
    // directly from Recharge's own getDynamicBundleItems source (see file
    // header) - this is what tells Recharge's backend these line items
    // belong together as one bundle purchase.
    const bundleGroupId = `${generateBundleGroupId(9)}:${this.bundleProductId}`;

    const items = this.selected.map((item) => ({
      id: item.variantId,
      quantity: item.quantity,
      properties: {
        _rc_bundle: bundleGroupId,
        _rc_bundle_variant: boxVariantId,
        _rc_bundle_parent: this.bundleHandle,
        _rc_bundle_collection_id: this.collectionId,
      },
    }));

    const cta = this.querySelector('[data-summary-cta]');
    if (cta) {
      cta.disabled = true;
      cta.dataset.originalText = cta.textContent;
      cta.textContent = 'Adding to cart…';
    }

    // Matches the theme's own add-to-cart convention (see product-form.js):
    // ask for the section(s) affected - the cart drawer among them - so the
    // response carries fresh rendered HTML. Without this, cart-drawer.js has
    // nothing to pre-morph and the drawer shows stale content until a full
    // page reload re-fetches everything from the server.
    const sections = [];
    this.dispatchEvent(new CartGroupedSections(sections));

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, sections: [...new Set(sections)].join(',') }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Cart add failed');
        }

        if (cta) cta.textContent = cta.dataset.originalText || 'Add Box to Cart';

        // Tells cart-drawer.js/cart-count.js to update themselves and
        // auto-open, exactly like a normal product add-to-cart - see
        // CartAddEvent in @theme/events.
        this.dispatchEvent(
          new CartAddEvent(response, this.id || 'build-your-box', {
            source: 'build-your-box',
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            sections: response.sections,
          })
        );

        this.selected = [];
        this.render();
      })
      .catch((err) => {
        console.error('build-your-box: Recharge bundle cart add failed', err);
        this.dispatchEvent(new CartErrorEvent(this.id || 'build-your-box', err.message));
        if (cta) {
          cta.disabled = false;
          cta.textContent = cta.dataset.originalText || 'Add Box to Cart';
        }
        alert('Something went wrong adding your box to the cart. Please try again.');
      });
  }
}

if (!customElements.get('build-your-box')) {
  customElements.define('build-your-box', BuildYourBox);
}
